import { useLazyQuery } from '@apollo/client';
import { debounce } from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { logger } from '../common/logger';
import { CompanySimilarityScore, GET_COMPANY_SIMILARITY_SCORES } from '../graphql/queries/companies';

interface CompanySimilarityState {
    isLoading: boolean;
    similarityData?: CompanySimilarityScore;
}

interface CompanySimilarityContextType {
    getCompanyData: (url: string) => CompanySimilarityState;
    requestCompanyData: (url: string) => void;
}

const CompanySimilarityContext = createContext<CompanySimilarityContextType | undefined>(undefined);

const debouncePeriodMs = 200;

interface CompanySimilarityProviderProps {
    children?: React.ReactNode;
}

export const CompanySimilarityProvider = ({ children }: CompanySimilarityProviderProps): JSX.Element => {
    const [data, setData] = useState<{ [url: string]: CompanySimilarityState }>({});
    const [urlsToFetch, setUrlsToFetch] = useState<Set<string>>(new Set());
    const [fetchingUrls, setFetchingUrls] = useState<Set<string>>(new Set());
    const [fetchData] = useLazyQuery<
        { companySimilarityScores: CompanySimilarityScore[] },
        { sourceUrls: string[]; additionalTargetCompanies?: Array<{ url: string; name: string }> }
    >(GET_COMPANY_SIMILARITY_SCORES);

    const debouncedFetchData = useMemo(
        () =>
            debounce(async (urls: Set<string>) => {
                const urlArray = Array.from(urls);
                try {
                    setUrlsToFetch((prev) => new Set([...Array.from(prev)].filter((url) => !urls.has(url))));
                    setFetchingUrls((prev) => new Set([...Array.from(prev), ...urlArray]));
                    const res = await fetchData({ variables: { sourceUrls: urlArray } });
                    if (res.error) {
                        throw res.error;
                    }
                    setData((prev) => {
                        let updated = { ...prev };
                        for (const url of urlArray) {
                            const similarityData = res.data.companySimilarityScores.find((c) => c.sourceUrl === url);
                            updated = {
                                ...updated,
                                [url]: { isLoading: false, similarityData }
                            };
                        }
                        return updated;
                    });
                    setFetchingUrls((prev) => new Set([...Array.from(prev)].filter((url) => !urlArray.includes(url))));
                } catch (err) {
                    logger.info('Error fetching company similarity data', { error: err.message, urls: urlArray });
                    setUrlsToFetch((prev) => new Set([...Array.from(prev), ...urlArray]));
                    setFetchingUrls((prev) => new Set([...Array.from(prev)].filter((url) => !urlArray.includes(url))));
                }
            }, debouncePeriodMs),
        [fetchData]
    );

    useEffect(() => {
        if (urlsToFetch.size > 0) {
            debouncedFetchData(urlsToFetch);
        }
    }, [urlsToFetch, debouncedFetchData]);

    const getSimilarityData = useCallback(
        (url: string): CompanySimilarityState => {
            if (!url) {
                return { isLoading: false };
            }
            if (data[url]) {
                return data[url];
            }
            const isLoading = fetchingUrls.has(url) || urlsToFetch.has(url);
            return { isLoading };
        },
        [data, fetchingUrls, urlsToFetch]
    );

    const requestCompanyData = useCallback(
        (url: string) => {
            if (!url || data[url] || fetchingUrls.has(url)) {
                return;
            }
            setUrlsToFetch((prev) => {
                if (prev.has(url)) {
                    return prev;
                }
                const next = new Set(prev);
                next.add(url);
                return next;
            });
        },
        [data, fetchingUrls]
    );

    const value = useMemo(
        () => ({
            getCompanyData: getSimilarityData,
            requestCompanyData
        }),
        [getSimilarityData, requestCompanyData]
    );

    return <CompanySimilarityContext.Provider value={value}>{children}</CompanySimilarityContext.Provider>;
};

export const useCompanySimilarity = (url: string): CompanySimilarityState => {
    const context = useContext(CompanySimilarityContext);
    if (!context) {
        throw new Error('useCompanySimilarity must be used within a CompanySimilarityProvider');
    }

    const { getCompanyData, requestCompanyData } = context;
    const state = getCompanyData(url);
    const shouldRequest = Boolean(url && !state.similarityData && !state.isLoading);

    useEffect(() => {
        if (shouldRequest) {
            requestCompanyData(url);
        }
    }, [shouldRequest, requestCompanyData, url]);

    if (shouldRequest) {
        return { ...state, isLoading: true };
    }

    return state;
};
